// +----------------------------------------------------------------------
// | Axios 实例：统一 baseURL、双 Header 注入、响应解包、401 拦截
// | 约定：后端返回 { code, msg, data }
// |   code === 0      → resolve(data)
// |   code === 401    → 清登录态并跳登录页
// |   其他             → reject(Error(msg))
// +----------------------------------------------------------------------
import axios from 'axios';
import { getAuth, clearAuth } from './storage';
import { API_BASE } from './config';

const request = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

// ------------------------------ 请求拦截：注入鉴权双 Header ------------------------------
request.interceptors.request.use(
  (config) => {
    const auth = getAuth();
    if (auth) {
      config.headers.username = auth.username;
      config.headers.token = auth.token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function handleUnauthorized() {
  clearAuth();
  const { pathname } = window.location;
  // 免登录页面不跳转，否则会把用户从「找回密码」流程里踢出去
  const FREE_PATHS = ['/login', '/register', '/forgot', '/reset'];
  if (!FREE_PATHS.includes(pathname)) {
    window.location.replace('/login');
  }
}

/**
 * 统一构造错误：带上后端返回的 code 与 data
 *
 * 为什么要带 code 而不是只给 msg：有些错误前端要分辨着处理，最典型的是
 * 429（免费额度用完）—— 该弹付费引导、并且不该给「重试」按钮，
 * 而这一判断绝不能靠匹配中文文案（文案一改就崩）。
 */
function apiError(msg, code = 0, data = null) {
  const err = new Error(msg);
  err.code = code;
  err.data = data;
  return err;
}

// ------------------------------ 响应拦截：解包 + 统一错误 ------------------------------
request.interceptors.response.use(
  (response) => {
    const body = response.data;

    // 非标准结构（比如被 nginx 返回了 HTML），直接当异常
    if (!body || typeof body !== 'object') {
      return Promise.reject(apiError('返回数据格式异常'));
    }

    if (body.code === 0) {
      return body.data;
    }

    if (body.code === 401) {
      handleUnauthorized();
      return Promise.reject(apiError(body.msg || '登录已过期，请重新登录', 401));
    }

    return Promise.reject(apiError(body.msg || '请求失败', body.code, body.data));
  },
  (error) => {
    const status = error.response && error.response.status;
    const body = error.response && error.response.data;

    if (status === 401) {
      handleUnauthorized();
      return Promise.reject(apiError('登录已过期，请重新登录', 401));
    }

    if (body && typeof body === 'object' && body.msg) {
      return Promise.reject(apiError(body.msg, body.code || status || 0, body.data));
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(apiError('想得有点久，稍后再试一下', error.code));
    }

    return Promise.reject(apiError('网络不太顺，稍后再试'));
  }
);

export default request;
